import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, GalleryHorizontalEnd } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import type { ImageComparisonState } from '@hooks/useImageComparison';

interface ImageComparisonDialogProps {
  comparison: ImageComparisonState;
}

const ImageComparisonDialog: React.FC<ImageComparisonDialogProps> = ({ comparison }) => {
  const { t } = useTranslation();
  const isCompare = comparison.comparisonMode === 'compare';

  return (
    <Dialog open={comparison.dialogOpen} onOpenChange={(open) => !open && comparison.closeDialog()}>
      <DialogContent className="revy-ui max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 p-0 sm:max-w-[900px]">
        <DialogHeader className="p-6">
          <DialogTitle className="flex items-center gap-2">
            {isCompare ? (
              <ArrowLeftRight className="size-5 text-primary" strokeWidth={1.9} />
            ) : (
              <GalleryHorizontalEnd className="size-5 text-secondary-foreground" strokeWidth={1.9} />
            )}
            <span className="revy-cjk text-base font-medium leading-[1.5]">
              {isCompare
                ? t('documents.imageComparison.compareResultTitle')
                : t('documents.imageComparison.sequenceResultTitle')}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto border-y p-6">
          {comparison.comparingImages ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <Spinner size="lg" label="" />
              <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
                {t('documents.imageComparison.aiAnalyzingPleaseWait')}
              </p>
            </div>
          ) : comparison.comparisonResult ? (
            <div>
              {isCompare && (
                <div className="mb-4 rounded-lg bg-muted p-3">
                  <p className="revy-cjk text-xs font-bold leading-[1.5] text-muted-foreground">
                    {t('documents.imageComparison.typeWithValue', {
                      type: t(`documents.imageComparison.types.${comparison.comparisonType}`),
                    })}
                  </p>
                </div>
              )}
              <p className="revy-cjk whitespace-pre-wrap text-base leading-[1.8]">
                {comparison.comparisonResult}
              </p>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertDescription className="revy-cjk leading-[1.6]">
                {t('documents.imageComparison.resultLoadFailed')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="p-6">
          <Button onClick={comparison.closeDialog}>
            <span className="revy-cjk">{t('common.close')}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageComparisonDialog;
